/**
 * SYR-90 — the research job table: every run, its status, evidence summary and
 * last error. Extracted from the AI Hub subsystem page in Phase 7 so it can sit
 * beside the runner in the CRM, where the outreach work actually happens.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Card,
    message,
    Popconfirm,
    Popover,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {
    DeleteOutlined,
    ExperimentOutlined,
    EyeOutlined,
    LinkOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { aiAdminApi, HypothesisJobRow } from '../../../services/ai-admin-api';
import { HYPOTHESIS_STATUS_COLORS } from './HypothesisRunnerPanel';
import { ResultPreviewModal } from './ResultPreviewModal';
import { ReviewSourcesModal } from './ReviewSourcesModal';
import { LeadResearchPanel } from '../public-hypothesis/LeadResearchPanel';

// Mirrors ACTIVE_JOB_STATUSES on the API, which is the set triggerForSubject
// rejects with a 409 to avoid re-crawling a job that is mid-flight. Kept
// identical so the buttons disappear exactly when the call would fail.
const ACTIVE_JOB_STATUSES = ['RESEARCHING', 'FORMING'];

const { Text } = Typography;

export const HypothesisJobsCard: React.FC<{ jwt: string }> = ({ jwt }) => {
    const [jobs, setJobs] = useState<HypothesisJobRow[]>([]);
    const [previewSubjectId, setPreviewSubjectId] = useState<number | null>(null);
    // The subject whose review sources are being filled in, for a run parked
    // at AWAITING_REVIEWS.
    const [sourcesFor, setSourcesFor] = useState<HypothesisJobRow | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    // This table is the ADMIN's research workspace: day-0 sign-up runs and
    // prospects. Public-tool traffic is deliberately absent — the Lead
    // generator queue below is its one home, and the same run appearing in two
    // tables with two row models on one page reads as a bug.
    const [origin, setOrigin] = useState<'admin' | 'signup' | 'prospect'>('admin');
    const [loading, setLoading] = useState(false);

    const load = useCallback(() => {
        if (!jwt) return;
        setLoading(true);
        aiAdminApi
            .listHypothesisJobs(jwt, undefined, origin)
            .then(setJobs)
            .catch(() => message.error('Failed to load hypothesis jobs'))
            .finally(() => setLoading(false));
    }, [jwt, origin]);

    useEffect(() => {
        load();
    }, [load]);

    const retry = async (jobId: number) => {
        try {
            await aiAdminApi.retryHypothesisJob(jwt, jobId);
            message.success('Job re-queued');
            load();
        } catch {
            message.error('Retry failed');
        }
    };

    // The only route out of RESEARCH_DONE + form_hypothesis = false, which no
    // formation lane picks up and nothing reclaims.
    const form = async (jobId: number) => {
        try {
            await aiAdminApi.formHypothesisJob(jwt, jobId);
            message.success('Queued for formation — the runner picks it up within a minute');
            load();
        } catch {
            message.error('Could not queue formation');
        }
    };

    // Finish a parked run with the sources just supplied. No crawl: the site,
    // the listings and the competitors come back out of the stored snapshot,
    // so this costs one synthesis.
    //
    // A refusal is a message, not an error — "nothing readable at those URLs"
    // is something to act on, and the job stays parked so other sources can be
    // tried.
    const continueRun = async (subjectId: number) => {
        try {
            const result = await aiAdminApi.continueHypothesisWithReviews(jwt, subjectId);
            if (result.resumed) {
                message.success(
                    `Continuing with ${result.sourcesFound} review source(s) — formation runs within a minute`,
                );
            } else {
                message.warning(result.reason || 'Could not continue');
            }
            load();
        } catch (e) {
            message.error((e as Error).message || 'Could not continue');
        }
    };

    // The same two subject-level actions the lead queue offers. They belong in
    // both because they act on the SUBJECT, which is what this table is keyed
    // on; the lead queue's other actions (result link, resend, to-CRM,
    // dismiss) are lead-level and have no meaning here — a job may have no
    // lead at all, and where it has several there is no single email to resend.
    const rerun = async (subjectId: number) => {
        try {
            await aiAdminApi.triggerHypothesisForSubject(jwt, subjectId, { mode: 'full' });
            message.success('Full research queued — the runner picks it up within a minute');
            load();
        } catch (e) {
            message.error((e as Error).message || 'Could not queue the re-run');
        }
    };

    // Formation only, against the research already stored: no crawl, no review
    // provider, no re-synthesis. The loop for iterating on the prompt.
    const reform = async (subjectId: number) => {
        try {
            await aiAdminApi.triggerHypothesisForSubject(jwt, subjectId, { mode: 'reform' });
            message.success('Re-forming from stored research');
            load();
        } catch (e) {
            message.error((e as Error).message || 'Could not re-form');
        }
    };

    // Deletes the SUBJECT, which is what owns the job and the hypothesis.
    // Public leads survive: they are detached and parked for review.
    const remove = async (subjectId: number) => {
        try {
            const result = await aiAdminApi.deleteHypothesisSubject(jwt, subjectId);
            message.success(
                result.leadsParked
                    ? `Deleted. ${result.leadsParked} lead(s) parked for review.`
                    : 'Deleted',
            );
            load();
        } catch {
            message.error('Delete failed');
        }
    };

    // Readable names for the chips. The Status column keeps the raw enum, which
    // is what you search the logs for; these are for scanning.
    const STATUS_LABEL: Record<string, string> = {
        QUEUED_RESEARCH: 'Queued',
        RESEARCHING: 'Researching',
        RESEARCH_DONE: 'Research done',
        FORMING: 'Forming',
        READY: 'Ready',
        INSUFFICIENT_DATA: 'Not enough data',
        FAILED: 'Failed',
        SKIPPED: 'Skipped',
    };
    // Pipeline order, not alphabetical, and fixed — so a chip does not move
    // under the cursor as counts change.
    const STATUS_ORDER = [
        'QUEUED_RESEARCH',
        'RESEARCHING',
        'RESEARCH_DONE',
        'FORMING',
        'READY',
        'INSUFFICIENT_DATA',
        'FAILED',
        'SKIPPED',
    ];

    // Counted from the rows actually loaded, NOT from a server-side tally: the
    // endpoint returns the most recent 200. Filtering is client-side for the
    // same reason — everything being counted is already here.
    const statusCounts = jobs.reduce<Record<string, number>>((acc, job) => {
        acc[job.status] = (acc[job.status] ?? 0) + 1;
        return acc;
    }, {});
    const visibleJobs =
        statusFilter === 'all'
            ? jobs
            : jobs.filter((job) => job.status === statusFilter);

    return (
        <Card
            title="Research jobs"
            size="small"
            extra={
                <Button
                    size="small"
                    icon={<ReloadOutlined style={{ marginRight: 4 }} />}
                    onClick={load}
                    loading={loading}
                >
                    Refresh
                </Button>
            }
        >
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                Sign-up runs and admin prospects. Requests from the public
                /hypothesis page are managed in the Lead generator queue below.
            </Text>
            <Space wrap style={{ marginBottom: 12 }}>
                {(
                    [
                        ['admin', 'All'],
                        ['signup', 'On sign-up'],
                        ['prospect', 'Prospects'],
                    ] as const
                ).map(([key, label]) => (
                    <Tag.CheckableTag
                        key={key}
                        checked={origin === key}
                        onChange={() => setOrigin(key)}
                    >
                        {label}
                    </Tag.CheckableTag>
                ))}
            </Space>

            {jobs.length ? (
                <Space wrap style={{ marginBottom: 16 }}>
                    <Tag.CheckableTag
                        checked={statusFilter === 'all'}
                        onChange={() => setStatusFilter('all')}
                    >
                        All {jobs.length}
                    </Tag.CheckableTag>
                    {STATUS_ORDER.filter((key) => statusCounts[key]).map((key) => {
                        const active = statusFilter === key;
                        const colour =
                            HYPOTHESIS_STATUS_COLORS[
                                key as HypothesisJobRow['status']
                            ];
                        return (
                            <Tag
                                key={key}
                                color={active ? colour : undefined}
                                onClick={() => setStatusFilter(active ? 'all' : key)}
                                style={{
                                    cursor: 'pointer',
                                    // Unselected chips keep the status colour as
                                    // an outline, so the palette still reads at a
                                    // glance without every tag competing with the
                                    // one you actually chose.
                                    borderColor: active ? undefined : colour,
                                    color: active ? undefined : colour,
                                    fontWeight: active ? 600 : 400,
                                }}
                            >
                                {STATUS_LABEL[key] ?? key}: {statusCounts[key]}
                            </Tag>
                        );
                    })}
                </Space>
            ) : null}

            <Table<HypothesisJobRow>
                dataSource={visibleJobs}
                rowKey="id"
                size="small"
                loading={loading}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                // The same research panel the lead queue opens, on the table
                // that actually covers every run. Fetched only when a row is
                // expanded.
                expandable={{
                    expandedRowRender: (row: HypothesisJobRow) => (
                        <LeadResearchPanel jwt={jwt} subjectId={row.subjectId} />
                    ),
                    rowExpandable: (row: HypothesisJobRow) => !!row.subjectId,
                }}
                columns={[
                    { title: 'ID', dataIndex: 'id', width: 60 },
                    { title: 'Subject', dataIndex: 'subjectId', width: 80 },
                    {
                        // Names, with the id kept in the tooltip: the number
                        // only matters when something has gone wrong and you
                        // are grepping a log for it.
                        title: 'Org',
                        dataIndex: 'organisationName',
                        width: 130,
                        ellipsis: true,
                        // Null for prospects and public-tool runs — they belong
                        // to no organisation, which is the point of them.
                        render: (name: string | null, row) =>
                            name ? (
                                <Tooltip title={`Organisation ${row.organisationId}`}>
                                    <span>{name}</span>
                                </Tooltip>
                            ) : (
                                <Text type="secondary">—</Text>
                            ),
                    },
                    {
                        title: 'Brand',
                        dataIndex: 'brandName',
                        width: 210,
                        render: (name: string | null, row) => (
                            <Space direction="vertical" size={0}>
                                <Text strong ellipsis style={{ maxWidth: 195 }}>
                                    {name ?? '—'}
                                </Text>
                                {/* The site under the name, so a prospect —
                                    which has no brand row and no org — is still
                                    identifiable by the only thing it has. */}
                                {row.websiteUrl ? (
                                    <a
                                        href={row.websiteUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        title={row.websiteUrl}
                                    >
                                        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                                            {row.websiteUrl
                                                .replace(/^https?:\/\/(www\.)?/, '')
                                                .replace(/\/$/, '')}
                                        </Text>
                                    </a>
                                ) : null}
                            </Space>
                        ),
                    },
                    {
                        title: 'Status',
                        dataIndex: 'status',
                        width: 160,
                        render: (status: HypothesisJobRow['status'], row) => {
                            const tag = <Tag color={HYPOTHESIS_STATUS_COLORS[status]}>{status}</Tag>;
                            return row.lastError ? (
                                <Popover content={row.lastError} overlayStyle={{ maxWidth: 400 }}>
                                    {tag}
                                </Popover>
                            ) : (
                                tag
                            );
                        },
                    },
                    {
                        // Invisible until now, and the reason a stalled job
                        // looked perfectly healthy in this table.
                        title: 'Forms',
                        dataIndex: 'formHypothesis',
                        width: 110,
                        render: (forms: boolean, row) =>
                            forms ? (
                                <Text type="secondary">yes</Text>
                            ) : (
                                <Tag color={row.status === 'RESEARCH_DONE' ? 'orange' : 'default'}>
                                    research only
                                </Tag>
                            ),
                    },
                    {
                        title: 'Evidence',
                        dataIndex: 'evidenceScore',
                        width: 200,
                        render: (score: number | null, row) => {
                            if (score === null) return <Text type="secondary">—</Text>;
                            const ev = row.evidenceJson;
                            const ecosystems = (ev?.ecosystemSources ?? [])
                                .map((s) => s.source)
                                .join(', ');
                            const summary = ev
                                ? `${ev.pagesFetched?.length ?? 0} pages · ${ev.reviewTextsCollected ?? 0} reviews (${ev.reviewSource ?? 'none'})${ecosystems ? ` · ${ecosystems}` : ''}`
                                : '';
                            return (
                                <span>
                                    <Tag color={score >= 5 ? 'green' : 'orange'}>{score}</Tag>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {summary}
                                    </Text>
                                </span>
                            );
                        },
                    },
                    {
                        title: 'Activated',
                        dataIndex: 'activatedAt',
                        width: 100,
                        render: (v: string | null) =>
                            v ? <Tag color="green">yes</Tag> : <Text type="secondary">no</Text>,
                    },
                    {
                        title: '',
                        key: 'actions',
                        width: 280,
                        render: (_: unknown, row) => (
                            <Space size={4} wrap>
                                {row.status === 'READY' && row.subjectId ? (
                                    // The same read-out the magic link renders,
                                    // so a job can be judged on its output
                                    // rather than on its status column.
                                    <Button
                                        size="small"
                                        icon={<EyeOutlined style={{ marginRight: 4 }} />}
                                        onClick={() => setPreviewSubjectId(row.subjectId)}
                                    >
                                        View
                                    </Button>
                                ) : null}

                                {row.status === 'RESEARCH_DONE' && !row.formHypothesis ? (
                                    <Popconfirm
                                        title="Form a hypothesis from this research?"
                                        description="The crawl is already done and paid for. This only turns on formation."
                                        okText="Form it"
                                        onConfirm={() => form(row.id)}
                                    >
                                        <Button
                                            size="small"
                                            type="primary"
                                            icon={<ThunderboltOutlined style={{ marginRight: 4 }} />}
                                        >
                                            Form
                                        </Button>
                                    </Popconfirm>
                                ) : null}

                                {/* The parked run's own two actions, in the
                                    order they are used: say where the
                                    customers are, then finish the run. */}
                                {row.status === 'AWAITING_REVIEWS' ? (
                                    <>
                                        <Button
                                            size="small"
                                            type="primary"
                                            icon={<LinkOutlined style={{ marginRight: 4 }} />}
                                            onClick={() => setSourcesFor(row)}
                                        >
                                            Add reviews
                                        </Button>
                                        <Popconfirm
                                            title="Continue this run?"
                                            description="Reads only the review sources on this subject, then synthesises from the research already stored. No re-crawl."
                                            okText="Continue"
                                            onConfirm={() => continueRun(row.subjectId)}
                                        >
                                            <Button
                                                size="small"
                                                icon={<PlayCircleOutlined style={{ marginRight: 4 }} />}
                                            >
                                                Continue
                                            </Button>
                                        </Popconfirm>
                                    </>
                                ) : null}

                                {/* Review pages, on any row rather than only
                                    a parked one: knowing where a brand's
                                    customers are is worth recording before the
                                    next run, not only when one has stalled. */}
                                {row.subjectId && row.status !== 'AWAITING_REVIEWS' ? (
                                    <Tooltip title="Review pages">
                                        <Button
                                            size="small"
                                            icon={<LinkOutlined />}
                                            onClick={() => setSourcesFor(row)}
                                        />
                                    </Tooltip>
                                ) : null}

                                {/* Re-crawl and re-form from scratch. Hidden
                                    mid-run: the API rejects it with a 409
                                    rather than double-spending the crawl, and
                                    a button that only ever errors is worse
                                    than no button. */}
                                {row.subjectId && !ACTIVE_JOB_STATUSES.includes(row.status) ? (
                                    <Popconfirm
                                        title="Re-run the full research?"
                                        description="Crawls the site and the review providers again, then re-forms. Replaces the stored research and the hypothesis. Costs AI and SerpAPI credit."
                                        okText="Re-run"
                                        onConfirm={() => rerun(row.subjectId)}
                                    >
                                        <Button
                                            size="small"
                                            icon={<ReloadOutlined style={{ marginRight: 4 }} />}
                                        >
                                            Re-run
                                        </Button>
                                    </Popconfirm>
                                ) : null}

                                {/* Needs stored research, not a particular
                                    status: an INSUFFICIENT_DATA job crawled
                                    perfectly well and is exactly the one worth
                                    re-forming once the gate or the prompt
                                    moves. */}
                                {row.subjectId &&
                                row.researchCompletedAt &&
                                !ACTIVE_JOB_STATUSES.includes(row.status) ? (
                                    <Popconfirm
                                        title="Re-form from the stored research?"
                                        description="No crawl and no new evidence — the same research through formation again. Replaces the current hypothesis."
                                        okText="Re-form"
                                        onConfirm={() => reform(row.subjectId)}
                                    >
                                        <Button
                                            size="small"
                                            icon={<ExperimentOutlined style={{ marginRight: 4 }} />}
                                        >
                                            Re-form
                                        </Button>
                                    </Popconfirm>
                                ) : null}

                                {['FAILED', 'SKIPPED', 'INSUFFICIENT_DATA'].includes(
                                    row.status,
                                ) ? (
                                    <Button size="small" onClick={() => retry(row.id)}>
                                        Retry
                                    </Button>
                                ) : null}

                                {row.subjectId ? (
                                    <Popconfirm
                                        title="Delete this research subject?"
                                        description="Removes the subject, its job and any hypothesis. Public leads are kept and parked for review."
                                        okText="Delete"
                                        okButtonProps={{ danger: true }}
                                        onConfirm={() => remove(row.subjectId)}
                                    >
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined style={{ marginRight: 4 }} />}
                                        >
                                            Delete
                                        </Button>
                                    </Popconfirm>
                                ) : null}
                            </Space>
                        ),
                    },
                ]}
            />

            <ReviewSourcesModal
                jwt={jwt}
                subjectId={sourcesFor?.subjectId ?? null}
                brandName={sourcesFor?.brandName ?? sourcesFor?.websiteUrl}
                onClose={() => setSourcesFor(null)}
                // Saving alone changes nothing — the pages still have to be
                // read. Continue is what does that, and it is the cheap path
                // rather than the full re-run the runner panel uses, because
                // the crawl behind a parked job is already stored.
                onSaved={async () => {
                    if (sourcesFor) await continueRun(sourcesFor.subjectId);
                }}
            />

            <ResultPreviewModal
                jwt={jwt}
                subjectId={previewSubjectId}
                onClose={() => setPreviewSubjectId(null)}
            />
        </Card>
    );
};
